import { useState } from 'react'
import { Link } from 'react-router-dom'
import { format, formatDistanceToNow } from 'date-fns'
import { Check, Copy, KeyRound, Play, RefreshCw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { BuildStatus, FeedsFrame, Pill, duration, num } from '../components/FeedsNav'
import { useBuildDestination, useDestinations, useRotateToken } from '../hooks/useFeeds'
import { REASON_LABEL, REASONS, type FeedBuild, type FeedDestination, type FeedFetch, type RotatedToken } from '../api/feedsApi'

const ago = (iso: string | null | undefined) => (iso ? formatDistanceToNow(new Date(iso), { addSuffix: true }) : '—')
const when = (iso: string | null | undefined) => (iso ? format(new Date(iso), 'MMM d, HH:mm') : '—')

function count(d: FeedDestination, kind: string, eligible: boolean) {
  return d.counts.filter((c) => c.kind === kind && c.eligible === eligible).reduce((t, c) => t + c.n, 0)
}

function StatCard({ title, children, tone }: { title: string; children: React.ReactNode; tone?: 'warn' | 'ok' }) {
  const ring = tone === 'warn' ? 'border-amber-300' : tone === 'ok' ? 'border-emerald-300' : ''
  return (
    <Card className={ring}>
      <CardHeader className="pb-1">
        <CardDescription className="text-xs uppercase tracking-wide">{title}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-1 text-sm">{children}</CardContent>
    </Card>
  )
}

function FetcherPill({ f }: { f: FeedFetch }) {
  if (f.fetcher === 'google') return <Pill tone="green">Google</Pill>
  if (f.fetcher === 'meta') return <Pill tone="blue">Meta</Pill>
  return <Pill tone="slate">{f.fetcher}</Pill>
}

function TokenReveal({ token, onClose }: { token: RotatedToken; onClose: () => void }) {
  const [copied, setCopied] = useState<string | null>(null)
  const copy = async (file: string) => {
    await navigator.clipboard.writeText(token.urls[file])
    setCopied(file)
    setTimeout(() => setCopied(null), 2500)
  }
  return (
    <div className="rounded-md border border-emerald-300 bg-emerald-50 p-4">
      <p className="text-sm font-medium text-emerald-900">
        New fetch URLs for {token.destination}. Copy them now — the token is stored hashed and cannot be shown again.
      </p>
      <p className="mt-1 text-xs text-emerald-900/80">
        The previous URLs stopped working. Update the Merchant Center feed (primary + supplemental) and the Google Ads page feed.
      </p>
      <div className="mt-3 space-y-2">
        {Object.keys(token.urls).map((file) => (
          <div key={file} className="flex items-center gap-2">
            <span className="w-28 shrink-0 text-xs font-medium text-emerald-900">{file}</span>
            <code className="flex-1 overflow-x-auto whitespace-nowrap rounded bg-white px-3 py-2 text-xs">{token.urls[file]}</code>
            <Button size="sm" variant="outline" onClick={() => copy(file)} aria-label={`Copy ${file} URL`}>
              {copied === file ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
            </Button>
          </div>
        ))}
      </div>
      <div className="mt-3 text-right">
        <Button size="sm" variant="ghost" onClick={onClose}>I have copied them</Button>
      </div>
    </div>
  )
}

function BuildsTable({ builds }: { builds: FeedBuild[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>#</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Started</TableHead>
          <TableHead>Duration</TableHead>
          <TableHead className="text-right">Individuals (ok / excl.)</TableHead>
          <TableHead className="text-right">Sets (ok / excl.)</TableHead>
          <TableHead className="text-right">Removed</TableHead>
          <TableHead>Sync</TableHead>
          <TableHead>Error</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {builds.length === 0 && (
          <TableRow><TableCell colSpan={9} className="py-6 text-center text-sm text-slate-500">No builds yet.</TableCell></TableRow>
        )}
        {builds.map((b) => (
          <TableRow key={b.id}>
            <TableCell className="tabular-nums">{b.id}</TableCell>
            <TableCell><BuildStatus status={b.status} /></TableCell>
            <TableCell title={b.startedAt}>{when(b.startedAt)}</TableCell>
            <TableCell className="tabular-nums">{b.status === 'running' ? ago(b.startedAt).replace(' ago', '') + '…' : duration(b.durationMs)}</TableCell>
            <TableCell className="text-right tabular-nums">
              {b.stats.individual ? `${num(b.stats.individual.eligible)} / ${num(b.stats.individual.excluded)}` : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {b.stats.set ? `${num(b.stats.set.eligible)} / ${num(b.stats.set.excluded)}` : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">{num(b.stats.removed)}</TableCell>
            <TableCell className="text-xs text-slate-500">
              {b.stats.timings?.sync_ms != null ? `synced (${duration(b.stats.timings.sync_ms)})` : 'skipped'}
            </TableCell>
            <TableCell className="max-w-xs truncate text-xs text-red-700" title={b.error ?? undefined}>{b.error ?? ''}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function DestinationSection({ d }: { d: FeedDestination }) {
  const build = useBuildDestination()
  const rotate = useRotateToken()
  const [confirmRotate, setConfirmRotate] = useState(false)
  const [revealed, setRevealed] = useState<RotatedToken | null>(null)

  const last = d.builds[0]
  const lastDone = d.builds.find((b) => b.status !== 'running')
  const running = last?.status === 'running'
  const eligible = count(d, 'individual', true) + count(d, 'set', true)
  const excluded = count(d, 'individual', false) + count(d, 'set', false)
  const lastFetch = d.fetches[0]
  const google = d.lastGoogleFetch

  const reasons = REASONS.map((r) => ({
    reason: r,
    individual: d.exclusions.filter((e) => e.reason === r && e.kind === 'individual').reduce((t, e) => t + e.n, 0),
    set: d.exclusions.filter((e) => e.reason === r && e.kind === 'set').reduce((t, e) => t + e.n, 0),
  })).filter((r) => r.individual + r.set > 0)

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold">{d.slug}</h2>
          <Pill tone="slate">{d.channel} · {d.country} · {d.currency} · {d.format}</Pill>
          {!d.enabled && <Pill tone="red">disabled</Pill>}
          {d.hasToken ? <Pill tone="green">fetch URL set</Pill> : <Pill tone="amber">no fetch URL</Pill>}
          <span className="text-xs text-slate-500">
            modes: {(d.variantPolicy.modes ?? ['framed']).join(', ')} · links to {d.linkBase}
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" disabled={running || build.isPending} onClick={() => build.mutate({ slug: d.slug, skipSync: false })}>
            <Play className="mr-1.5 h-3.5 w-3.5" /> Build now
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={running || build.isPending}
            title="Reuse the last Shopify sync and prices; only re-render items (after a registry or template change)."
            onClick={() => build.mutate({ slug: d.slug, skipSync: true })}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Re-render (skip sync)
          </Button>
          <Button size="sm" variant="outline" disabled={rotate.isPending} onClick={() => setConfirmRotate(true)}>
            <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Rotate token
          </Button>
        </div>
      </div>

      {revealed && <TokenReveal token={revealed} onClose={() => setRevealed(null)} />}

      <AlertDialog open={confirmRotate} onOpenChange={setConfirmRotate}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rotate the fetch token for {d.slug}?</AlertDialogTitle>
            <AlertDialogDescription>
              {d.hasToken
                ? 'The current fetch URLs stop working immediately. Merchant Center and the Google Ads page feed will fail to fetch until you paste the new URLs into them.'
                : 'This creates the first fetch URLs for this destination.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => rotate.mutate(d.slug, { onSuccess: setRevealed })}>
              {d.hasToken ? 'Rotate' : 'Create URLs'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard title="Eligible items">
          <div className="text-2xl font-semibold tabular-nums">{num(eligible)}</div>
          <div className="text-xs text-slate-500">
            {num(count(d, 'individual', true))} individual · {num(count(d, 'set', true))} sets
          </div>
          <Link to={`/app/feeds/items?destination=${d.slug}&eligible=true`} className="text-xs text-sky-700 hover:underline">Browse</Link>
        </StatCard>
        <StatCard title="Excluded items">
          <div className="text-2xl font-semibold tabular-nums">{num(excluded)}</div>
          <div className="text-xs text-slate-500">
            {num(count(d, 'individual', false))} individual · {num(count(d, 'set', false))} sets
          </div>
          <Link to={`/app/feeds/items?destination=${d.slug}&eligible=false`} className="text-xs text-sky-700 hover:underline">Browse</Link>
        </StatCard>
        <StatCard title="Last build" tone={last?.status === 'failed' ? 'warn' : undefined}>
          {last ? (
            <>
              <div className="flex items-center gap-2">
                <BuildStatus status={last.status} />
                <span className="text-slate-700">#{last.id}</span>
              </div>
              <div className="text-xs text-slate-500">
                started {ago(last.startedAt)}
                {last.status !== 'running' && <> · took {duration(last.durationMs)}</>}
              </div>
              {last.status === 'failed' && <div className="truncate text-xs text-red-700" title={last.error ?? ''}>{last.error}</div>}
              {last.status === 'running' && lastDone && (
                <div className="text-xs text-slate-500">previous: #{lastDone.id} {lastDone.status}, {duration(lastDone.durationMs)}</div>
              )}
            </>
          ) : (
            <div className="text-slate-500">Never built.</div>
          )}
        </StatCard>
        <StatCard title="Fetched by" tone={google ? 'ok' : 'warn'}>
          {google ? (
            <div>
              <span className="font-medium text-emerald-800">Google fetched {ago(google.at)}</span>
              <div className="text-xs text-slate-500">{google.file} · HTTP {google.status} · {num(google.rows)} rows</div>
            </div>
          ) : (
            <div className="font-medium text-amber-800">Google has not fetched this feed yet.</div>
          )}
          {lastFetch && (lastFetch.id !== google?.id) && (
            <div className="text-xs text-slate-500">
              Last fetch {ago(lastFetch.at)} ({lastFetch.fetcher}): <span className="break-all">{lastFetch.userAgent || 'no user agent'}</span>
            </div>
          )}
          {!lastFetch && <div className="text-xs text-slate-500">No fetches logged.</div>}
        </StatCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Exclusions by reason</CardTitle>
            <CardDescription>Each excluded item records the first reason it failed, in this order.</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Individual</TableHead>
                  <TableHead className="text-right">Sets</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {reasons.length === 0 && (
                  <TableRow><TableCell colSpan={3} className="py-6 text-center text-sm text-slate-500">Nothing excluded.</TableCell></TableRow>
                )}
                {reasons.map((r) => (
                  <TableRow key={r.reason}>
                    <TableCell>
                      <Link to={`/app/feeds/items?destination=${d.slug}&eligible=false&reason=${r.reason}`} className="font-mono text-xs hover:underline">
                        {r.reason}
                      </Link>
                      <div className="text-xs text-slate-500">{REASON_LABEL[r.reason]}</div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{num(r.individual)}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(r.set)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Recent fetches</CardTitle>
            <CardDescription>The last 20 downloads of any feed file for this destination.</CardDescription>
          </CardHeader>
          <CardContent className="max-h-96 overflow-auto p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>File</TableHead>
                  <TableHead>HTTP</TableHead>
                  <TableHead className="text-right">Rows</TableHead>
                  <TableHead>By</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {d.fetches.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="py-6 text-center text-sm text-slate-500">No fetches yet.</TableCell></TableRow>
                )}
                {d.fetches.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell title={f.at}>{when(f.at)}</TableCell>
                    <TableCell className="font-mono text-xs">{f.file}</TableCell>
                    <TableCell className={f.status >= 400 ? 'text-red-700' : undefined}>{f.status}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(f.rows)}</TableCell>
                    <TableCell title={f.userAgent ?? ''}><FetcherPill f={f} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Builds</CardTitle>
          <CardDescription>The last 10. The nightly worker and the buttons above run the same pipeline.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <BuildsTable builds={d.builds} />
        </CardContent>
      </Card>
    </section>
  )
}

export function DestinationsPage() {
  const { data, isLoading } = useDestinations()
  return (
    <FeedsFrame
      title="Feeds"
      description={
        <>
          Product feeds built from what the storefront renders: one row per artwork mode at its default offer, plus gallery-wall
          sets. Merchant Center and Google Ads fetch them on a schedule from token URLs; a build syncs Shopify, prices in the
          destination&apos;s currency and re-renders every item.
        </>
      }
    >
      {isLoading && <Skeleton className="h-64 w-full" />}
      {!isLoading && data?.data.length === 0 && <p className="text-sm text-slate-500">No destinations configured.</p>}
      <div className="space-y-10">
        {data?.data.map((d) => <DestinationSection key={d.slug} d={d} />)}
      </div>
    </FeedsFrame>
  )
}
